import React, { Fragment } from "react"
import PropTypes from "prop-types"
import styled, { css } from "react-emotion"

import EditableWrapper from "ui/utils/EditableWrapper"
import { Input, Button, Text, TextArea, Dropdown, Icon, DatePicker, Checkbox, Section } from "ui"

import ReactFilestack from "lazyComponents/ReactFilestack"
import moment from "moment"
import "react-datepicker/dist/react-datepicker.css"
import RecordDropdown from "./RecordDropdown"
import RecordLookup from "./RecordLookup"
import KeyValueList from "../../studio/ui/KeyValueList"

const EditIcon = styled(Icon)`
    position: absolute;
    right: -7px;
    top: 12px;
    font-size: 13px;
    display: none;
    cursor: pointer;
    padding: 5px 10px;

    &:hover {
        display: inline-block;
    }
`

const hover = css`
    &:hover + i {
        display: inline-block;
    }
`

const hoverChildren = css`
    &:hover > i {
        display: inline-block;
    }
    width: 100%;
    height: 100%;
    padding-right: 10px;
    display: inline-block;
`

const InlineImage = styled("img")`
    max-height: 150px;
    max-width: 150px;
`

class Attribute extends React.Component {
    // This component handles rendering attributes of records,
    // either as static display, inline-editable, pre-filled form
    // fields, or even blank form fields

    render() {
        if (!this.props.field) {
            return this.renderTextAttribute()
        }
        if (this.props.field.type === "string") {
            return this.renderTextAttribute()
        }
        if (this.props.field.type === "key_value") {
            return this.renderKeyValueAttribute()
        }
        if (this.props.field.type === "long_text") {
            return this.renderLongTextAttribute()
        }
        if (this.props.field.type === "integer") {
            // TODO: Need to add validation here
            return this.renderTextAttribute()
        }
        if (this.props.field.type === "autoid") {
            // TODO: Need to add validation here
            return this.renderAutoIDAttribute()
        }
        if (this.props.field.type === "dropdown") {
            return this.renderDropdownAttribute()
        }
        if (this.props.field.type === "url") {
            return this.renderUrlAttribute()
        }
        if (this.props.field.type === "image") {
            return this.renderImageAttribute()
        }
        if (this.props.field.type === "date") {
            return this.renderDateAttribute()
        }
        if (this.props.field.type === "copy") {
            return this.renderCopyAttribute()
        }
        if (this.props.field.type === "lookup") {
            return this.renderLookupAttribute()
        }
        if (this.props.field.type === "checkbox") {
            return this.renderCheckboxAttribute()
        }
        if (this.props.field.type === "password") {
            return this.renderPasswordAttribute()
        }
        return this.renderTextAttribute()
    }

    // TODO:
    // rich_text
    // currency

    renderCheckboxAttribute() {
        return (
            <Checkbox
                onChange={val => {
                    this.props.editable && this.onChange(val)
                }}
                value={this.props.children}
            />
        )
    }

    renderLookupAttribute() {
        var form = (
            <RecordDropdown
                margin={"none"}
                objectId={this.props.field.options && this.props.field.options.lookup_target}
                value={this.props.children}
                onChange={this.onChange}
                autoFocus={this.props.inline}
            />
        )
        var display = <RecordLookup margin={"none"} recordId={this.props.children} />

        if (this.props.editable & this.props.inline) {
            return (
                <EditableWrapper
                    input={({ end }) => React.cloneElement(form, { onBlur: end })}
                    value={({ start }) => (
                        <span className={hoverChildren}>
                            {display}
                            <EditIcon icon="pencil" onClick={start} />
                        </span>
                    )}
                />
            )
        }
        if (this.props.editable) {
            return form
        }
        return display
    }

    renderCopyAttribute() {
        var form = (
            <Input
                margin={"none"}
                onChange={event => this.onChange(event.target.value)}
                value={this.props.children || ""}
                disabled
            />
        )
        var display = <Text margin={"none"}>{this.props.children}</Text>

        if (this.props.editable & this.props.inline) {
            return display
        }
        if (this.props.editable) {
            return form
        }
        return display
    }

    renderDateAttribute() {
        let dateval = moment(this.props.children, "DD/MM/YYYY")
        // let showVal = dateval.isValid() ? dateval.format("DD/MM/YYYY") : this.props.children + "."
        if (!dateval.isValid()) dateval = moment()

        var form = (
            <DatePicker
                selected={dateval || null}
                onChange={item => {
                    this.onChange(item.format("DD/MM/YYYY"))
                }}
                dateFormat="DD/MM/YYYY"
            />
        )
        var display = <Text margin={"none"}>{this.props.children}</Text>

        if (this.props.editable & this.props.inline) {
            return (
                <EditableWrapper
                    input={({ end, ref }) =>
                        React.cloneElement(form, { innerRef: me => (ref.ref = me), onBlur: end })
                    }
                    value={({ start }) => React.cloneElement(display, { onClick: start })}
                />
            )
        }
        if (this.props.editable) {
            return form
        }
        return display
    }

    renderImageAttribute() {
        var form = (
            <ReactFilestack
                apikey="Arfn2PRozQ26eRkdQy74Ez"
                options={{
                    fromSources: [
                        "local_file_system",
                        "url",
                        "webcam",
                        "imagesearch",
                        "facebook",
                        "instagram",
                        "googledrive",
                        "dropbox",
                    ],
                    accept: "image/*",
                    maxFiles: 1,
                    storeTo: {
                        location: "s3",
                    },
                }}
                onSuccess={value => {
                    this.onChange(value.filesUploaded[0].url)
                }}
                render={({ onPick }) => (
                    <Button padding="small" onClick={onPick}>
                        Upload
                    </Button>
                )}
            />
        )
        let url = this.props.children
        if (url && url.indexOf("http") === -1 && url.indexOf("data:") === -1) {
            url = "http://" + url
        }

        var display = <InlineImage className={hover} src={url} />

        if (this.props.editable & this.props.inline) {
            return (
                <EditableWrapper
                    input={({ end, ref }) =>
                        React.cloneElement(form, {
                            innerRef: me => (ref.ref = me),
                            onBlur: end,
                            onSuccess: value => {
                                this.onChange(value.filesUploaded[0].url)
                                end()
                            },
                        })
                    }
                    value={({ start }) => (
                        <span className={hoverChildren}>
                            {display}
                            <EditIcon icon="pencil" onClick={start} />
                        </span>
                    )}
                />
            )
        }
        if (this.props.editable) {
            return (
                <Section margin="none" padding="none">
                    {this.props.children && display}
                    {form}
                </Section>
            )
        }
        return display
    }

    renderUrlAttribute() {
        var form = (
            <Input
                margin={"none"}
                onChange={event => this.onChange(event.target.value)}
                value={this.props.children || ""}
            />
        )
        let url = this.props.children
        if (url && url.indexOf("http") === -1) {
            url = "http://" + url
        }

        var display = (
            <a className={hover} href={url}>
                <Text margin={"none"}>{this.props.children}</Text>
            </a>
        )

        if (this.props.editable & this.props.inline) {
            return (
                <EditableWrapper
                    input={({ end, ref }) =>
                        React.cloneElement(form, { innerRef: me => (ref.ref = me), onBlur: end })
                    }
                    value={({ start }) => (
                        <Fragment>
                            {display}
                            <EditIcon icon="pencil" onClick={start} />
                        </Fragment>
                    )}
                />
            )
        }
        if (this.props.editable) {
            return form
        }
        return display
    }

    renderAutoIDAttribute() {
        var form = (
            <Input
                margin={"none"}
                onChange={event => this.onChange(event.target.value)}
                value={this.props.children || ""}
                disabled
                placeholder="generated value"
            />
        )
        var display = <Text margin={"none"}>{this.props.children}</Text>

        if (this.props.editable & this.props.inline) {
            return display
        }
        if (this.props.editable) {
            return form
        }
        return display
    }

    renderLongTextAttribute() {
        var form = (
            <TextArea
                margin={"none"}
                onChange={event => this.onChange(event.target.value)}
                children={this.props.children || ""}
            />
        )
        var display = <Text margin={"none"}>{this.props.children}</Text>

        if (this.props.editable & this.props.inline) {
            return (
                <EditableWrapper
                    input={({ end, ref }) =>
                        React.cloneElement(form, { innerRef: me => (ref.ref = me), onBlur: end })
                    }
                    value={({ start }) => React.cloneElement(display, { onClick: start })}
                />
            )
        }
        if (this.props.editable) {
            return form
        }
        return display
    }
    renderKeyValueAttribute() {
        const options = this.props.field.options || {}
        const form = (
            <KeyValueList
                keyName={options.keyName || "label"}
                valueName={options.valueName || "value"}
                onChange={this.onChange}
                value={this.props.children || []}
            />
        )

        return form
    }

    renderTextAttribute() {
        var inlineForm = <Input margin={"none"} defaultValue={this.props.children || ""} />

        var form = (
            <Input
                margin={"none"}
                onChange={event => this.onChange(event.target.value)}
                defaultValue={this.props.children || ""}
                value={this.props.children || ""}
            />
        )

        var display = <Text margin={"none"}>{this.props.children}</Text>

        if (this.props.editable & this.props.inline) {
            return (
                <EditableWrapper
                    input={({ end, ref }) =>
                        React.cloneElement(inlineForm, {
                            innerRef: me => (ref.ref = me),
                            onBlur: end,
                        })
                    }
                    value={({ start }) => React.cloneElement(display, { onClick: start })}
                    onChange={this.onChange}
                />
            )
        }
        if (this.props.editable) {
            return form
        }
        return display
    }

    renderPasswordAttribute() {
        var form = (
            <Input
                type="password"
                margin={"none"}
                onChange={event => this.onChange(event.target.value)}
                value={this.props.children || ""}
            />
        )
        var display = <Text margin={"none"}>********</Text>

        if (this.props.editable & this.props.inline) {
            return (
                <EditableWrapper
                    input={({ end, ref }) =>
                        React.cloneElement(form, { innerRef: me => (ref.ref = me), onBlur: end })
                    }
                    value={({ start }) => React.cloneElement(display, { onClick: start })}
                />
            )
        }
        if (this.props.editable) {
            return form
        }
        return display
    }

    renderDropdownAttribute() {
        var trueValue = this.props.children
        var iconWrap = option => (
            <Fragment>
                {option.icon}
                {option.label}
            </Fragment>
        )
        const options = this.props.field.options.options
        options.forEach(option => {
            if (option.value === this.props.children) trueValue = iconWrap(option)
        })
        var form = (
            <Dropdown
                margin={"none"}
                autoFocus={this.props.inline}
                options={options}
                onChange={this.onChange}
                value={this.props.children}
                optionRenderer={iconWrap}
                valueRenderer={iconWrap}
            />
        )
        var display = <Text margin={"none"}>{trueValue}</Text>

        if (this.props.editable & this.props.inline) {
            return (
                <EditableWrapper
                    input={({ end }) => React.cloneElement(form, { onBlur: end })}
                    value={({ start }) => React.cloneElement(display, { onClick: start })}
                />
            )
        }
        if (this.props.editable) {
            return form
        }
        return display
    }

    onChange = value => {
        if (this.props.onChange) {
            this.props.onChange(value)
        }
    }
}

Attribute.propTypes = {
    editable: PropTypes.bool,
    inline: PropTypes.bool,
    field: PropTypes.object,
    onChange: PropTypes.func,
}

export default Attribute



// WEBPACK FOOTER //
// ./src/features/records/components/Attribute.js